import { useEffect, useState, ChangeEvent } from "react";
import axios from "axios";
import dayjs, { Dayjs } from "dayjs";
import { API_BASE_PATH } from "../../environment";
import { toast } from "react-toastify";

// Define the experience base URL separately
const EXPERIENCE_BASE_URL = `${API_BASE_PATH}/experience`;

export interface ExperienceFormData {
  id?: number;
  employee_id?: string;
  previous_company_name: string;
  designation: string;
  start_date: string;
  end_date: string;
  working_present: boolean;
}

const initialExperienceForm: ExperienceFormData = {
  previous_company_name: "",
  designation: "",
  start_date: "",
  end_date: "",
  working_present: false,
};

// ====== Error Extractor ======
const extractErrorMessage = (error: any) => {
  const detail = error?.response?.data?.detail;
  let details = error?.response?.data?.details;
  const message = error?.response?.data?.message;

  if (typeof details === "string") {
    details = details.replace(/^\d+\s*:\s*/, ""); // strip numeric codes
  }

  return detail || details || message;
};

// 🔹 Hook now accepts token
export const useExperienceInfo = (
  employeeId: string | null,
  token: string | null
) => {
  const [formDataExperience, setFormDataExperience] =
    useState<ExperienceFormData>({
      previous_company_name: "",
      designation: "",
      start_date: "",
      end_date: "",
      working_present: false,
    });

  const [experienceList, setExperienceList] = useState<ExperienceFormData[]>(
    []
  );

  const getExperienceInfo = async () => {
    if (!employeeId) return;
    try {
      const res = await axios.get(`${EXPERIENCE_BASE_URL}/${employeeId}`, {
        headers: {
          accept: "application/json",
          authorization: "Bearer " + token,
        },
      });

      setExperienceList(
        Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.data)
          ? res.data.data
          : []
      );
    } catch (err: any) {
      const message = extractErrorMessage(err);
      console.error("❌ Error fetching experience info", message);
      toast.error(message);
      setExperienceList([]); // ✅ Fallback to empty array on error
    }
  };

  useEffect(() => {
    getExperienceInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, token]);

  const handleChangeExperience = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormDataExperience((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleDateChangeExperience = (
    name: "start_date" | "end_date",
    date: Dayjs | null
  ) => {
    if (date) {
      setFormDataExperience((prev) => ({
        ...prev,
        [name]: date.format("YYYY-MM-DD"),
      }));
    }
  };

  const handleSubmitExperience = async () => {
    if (!employeeId) {
      toast.error("Employee ID is missing");
      return;
    }

    const {
      previous_company_name,
      designation,
      start_date,
      end_date,
      working_present,
    } = formDataExperience;

    if (
      !previous_company_name ||
      !designation ||
      !start_date ||
      (!working_present && !end_date)
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      if (formDataExperience.id) {
        try {
          const payload: any = {
            previous_company_name,
            designation,
            start_date,
            working_present,
          };

          if (!working_present) {
            payload.end_date = end_date;
          }

          await axios.put(
            `${EXPERIENCE_BASE_URL}/${formDataExperience.id}`,
            payload,
            {
              headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                authorization: "Bearer " + token,
              },
            }
          );
          toast.success("Experience information updated");
        } catch (putError: any) {
          if (putError.response?.status === 404) {
            const payload: any = {
              employee_id: employeeId,
              previous_company_name,
              designation,
              start_date,
              working_present,
            };

            if (!working_present) {
              payload.end_date = end_date;
            }

            await axios.post(`${EXPERIENCE_BASE_URL}/`, payload, {
              headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                authorization: "Bearer " + token,
              },
            });
            toast.success("Experience information added");
          } else {
            const message = extractErrorMessage(putError);
            console.error("❌ Error submitting experience info", message);
            toast.error(message);
            throw putError;
          }
        }
      } else {
        const payload: any = {
          employee_id: employeeId,
          previous_company_name,
          designation,
          start_date,
          working_present,
        };

        if (!working_present) {
          payload.end_date = end_date;
        }

        await axios.post(`${EXPERIENCE_BASE_URL}/`, payload, {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            authorization: "Bearer " + token,
          },
        });
        toast.success("Experience information added");
      }

      setFormDataExperience(initialExperienceForm);

      await getExperienceInfo();
    } catch (err: any) {
      const message = extractErrorMessage(err);
      console.error("❌ Error submitting experience info:", message);
      toast.error(message);
    }
  };

  const handleResetExperience = () => {
    setFormDataExperience(initialExperienceForm);
  };

  const handleEditExperience = (record: ExperienceFormData) => {
    setFormDataExperience({ ...record });
  };

  return {
    formDataExperience,
    setFormDataExperience,
    handleResetExperience,
    experienceList,
    handleChangeExperience,
    handleDateChangeExperience,
    handleSubmitExperience,
    handleEditExperience,
    getExperienceInfo,
  };
};
